import React, { useState } from 'react';
import { ArrowRight, Facebook, Instagram, LoaderCircle, Music2 } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { customerSupabase } from '../context/AuthContext';
import OptimizedImage from './common/OptimizedImage';

const socialLinks = [
  { name: 'Instagram', Icon: Instagram, url: process.env.REACT_APP_SOCIAL_INSTAGRAM, fallback: 'https://www.instagram.com/cab_store1928/' },
  { name: 'Facebook', Icon: Facebook, url: process.env.REACT_APP_SOCIAL_FACEBOOK, fallback: 'https://www.facebook.com/profile.php?id=61593498029917&ref=NONE_xav_ig_profile_page_web#' },
  { name: 'TikTok', Icon: Music2, url: process.env.REACT_APP_SOCIAL_TIKTOK, fallback: 'https://www.tiktok.com/' },
  { name: 'X', Icon: null, url: process.env.REACT_APP_SOCIAL_X, fallback: 'https://x.com/' },
];

const footerGroups = [
  {
    title: 'Shop',
    links: [
      { label: 'Shop', to: '/shop' },
      { label: 'Jerseys', to: '/shop?category=Jerseys' },
      { label: 'Training', to: '/shop?category=Training' },
      { label: 'Lifestyle', to: '/shop?category=Lifestyle' },
      { label: 'Accessories', to: '/shop?category=Accessories' },
    ],
  },
  {
    title: 'Custom',
    links: [
      { label: 'Create Your Jersey', to: '/#customizer-title' },
      { label: 'Personalize', to: '/#customizer-personalization' },
      { label: 'Custom Kits', to: '/#customizer-title' },
    ],
  },
  {
    title: 'Club',
    links: [
      { label: 'About CAB', to: '/club' },
      { label: 'History', to: '/club#history' },
      { label: 'The North', to: '/club#the-north' },
      { label: 'Official Store', to: '/shop' },
    ],
  },
  {
    title: 'Help',
    links: [
      { label: 'Contact', to: '/help/contact' },
      { label: 'Shipping', to: '/help/shipping' },
      { label: 'Returns', to: '/help/returns' },
      { label: 'FAQ', to: '/help/faq' },
      { label: 'Size Guide', to: '/help/size-guide' },
    ],
  },
];

const Footer = () => {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [subscriptionState, setSubscriptionState] = useState('idle');
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  const activeCategory = new URLSearchParams(location.search).get('category');

  const isCurrentLink = (to) => {
    const [pathname, query = ''] = to.split('?');
    const hashlessPath = pathname.split('#')[0];
    if (hashlessPath !== location.pathname) return false;
    if (!query) return !to.includes('#') || location.hash === `#${to.split('#')[1]}`;
    const category = new URLSearchParams(query).get('category');
    return activeCategory?.toLowerCase() === category?.toLowerCase();
  };

  const subscribe = async (event) => {
    event.preventDefault();
    if (subscriptionState === 'loading') return;

    const normalizedEmail = email.trim().toLowerCase();
    setSubscriptionState('loading');
    setSubscriptionMessage('');
    try {
      const { error } = await customerSupabase.from('newsletter_subscribers').insert({ email: normalizedEmail });
      if (error?.code === '23505') {
        setSubscriptionState('success');
        setSubscriptionMessage('You are already on the list. We will keep you in the colors.');
        return;
      }
      if (error) throw error;
      setSubscriptionState('success');
      setSubscriptionMessage('You are on the list. Watch your inbox for the next CAB letter.');
      setEmail('');
    } catch {
      setSubscriptionState('error');
      setSubscriptionMessage('We could not save your subscription. Please try again shortly.');
    }
  };

  return (
    <footer className="cab-footer">
      <section className="cab-footer__cta">
        <div className="cab-footer__cta-glow" aria-hidden="true" />
        <div className="cab-container cab-footer__cta-inner">
          <div>
            <span className="cab-footer__eyebrow">CAB STORE / BIZERTE / 1928</span>
            <h2>JOIN THE<br /><em>FAMILY.</em></h2>
            <p>Create your CAB Store membership and enjoy exclusive benefits while supporting the club you love.</p>
          </div>
          <div className="cab-footer__cta-action">
            <span className="cab-footer__cta-action-label">MEMBERSHIP / 01</span>
            <Link to="/membership" className="cab-footer__cta-button">
              <span>Create your membership</span>
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
            <small>Official CAB Store community</small>
          </div>
        </div>
      </section>

      <div className="cab-footer__main">
        <div className="cab-container">
          <div className="cab-footer__topline"><span>CLUB ATHLÉTIQUE BIZERTIN</span><span>THE NORTH / EST. 1928</span></div>
          <div className="cab-footer__grid">
            <div className="cab-footer__identity">
              <Link to="/shop" className="cab-logo-mark" aria-label="CAB Store official shop">
                <OptimizedImage src="/assets/cab-logo.png" alt="" sizes="40px" />
                <span><strong>CAB STORE</strong><small>OFFICIAL CLUB STORE</small></span>
              </Link>
              <p className="cab-footer__text">Carry the colors. Live the passion. Represent the North.</p>
              <nav className="cab-footer__socials" aria-label="CAB Store social media">
                {socialLinks.map(({ name, Icon, url, fallback }) => (
                  <a
                    key={name}
                    href={url || fallback}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={url ? `Follow CAB on ${name}` : `Open ${name}`}
                    title={url ? `Follow CAB on ${name}` : `Open ${name}`}
                  >
                    {Icon ? <Icon size={17} aria-hidden="true" /> : <span className="cab-footer__x-icon" aria-hidden="true">X</span>}
                  </a>
                ))}
              </nav>
              <p className="cab-footer__social-note">Official profile links can be set with the REACT_APP_SOCIAL_* environment variables.</p>
            </div>

            <nav className="cab-footer__links" aria-label="Footer navigation">
              {footerGroups.map(({ title, links }) => (
                <div key={title}>
                  <h4>{title}</h4>
                  {links.map(({ label, to }) => (
                    <Link key={label} to={to} aria-current={isCurrentLink(to) ? 'page' : undefined}>{label}</Link>
                  ))}
                </div>
              ))}
            </nav>
          </div>

          <section className="cab-footer__newsletter" aria-labelledby="cab-letter-heading">
            <div><span className="cab-footer__eyebrow">THE CAB LETTER</span><h3 id="cab-letter-heading">STAY IN THE COLORS.</h3><p>Be the first to discover new kits, exclusive drops and CAB STORE releases.</p></div>
            <form onSubmit={subscribe}>
              <label className="cab-footer__email-label" htmlFor="cab-newsletter-email">Email address</label>
              <input id="cab-newsletter-email" type="email" name="email" autoComplete="email" value={email} onChange={(event) => { setEmail(event.target.value); if (subscriptionState !== 'loading') { setSubscriptionState('idle'); setSubscriptionMessage(''); } }} placeholder="Your email address" required aria-describedby="cab-newsletter-feedback" />
              <button type="submit" disabled={subscriptionState === 'loading'}>
                {subscriptionState === 'loading' ? <><LoaderCircle size={16} className="cab-footer__spinner" aria-hidden="true" /> Joining</> : <>Join <ArrowRight size={16} aria-hidden="true" /></>}
              </button>
            </form>
            <p id="cab-newsletter-feedback" className={`cab-footer__newsletter-message is-${subscriptionState}`} role={subscriptionState === 'error' ? 'alert' : 'status'} aria-live="polite">{subscriptionMessage}</p>
          </section>

          <div className="cab-footer__bar">
            <span>© {new Date().getFullYear()} CAB STORE · Official Club Store</span>
            <nav aria-label="Legal information"><Link to="/help/privacy">Privacy Policy</Link><Link to="/help/terms">Terms &amp; Conditions</Link></nav>
            <strong>Made for the North.</strong>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;